import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Download, CheckCircle2, RotateCcw, Lock, FileText, Wallet, Plus, Trash2, Send, Bell, Eye } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
}
function fmtEuro(n) {
  const s = (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${s} €`;
}

const STATUS_STYLE = {
  draft:     "bg-slate-100 text-slate-700 border-slate-200",
  issued:    "bg-blue-50 text-blue-700 border-blue-200",
  paid:      "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

export default function InvoiceView() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cnOpen, setCnOpen] = useState(false);
  const [cnReason, setCnReason] = useState("");
  const [cnSubmitting, setCnSubmitting] = useState(false);
  const [payments, setPayments] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", method: "virement", reference: "", note: "" });
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderTemplate, setReminderTemplate] = useState("friendly");
  const [reminderSending, setReminderSending] = useState(false);
  const canWrite = hasPermission("invoices.write");
  const canValidate = hasPermission("invoices.validate");

  const sendManualReminder = async () => {
    if (!inv?.id) return;
    setReminderSending(true);
    try {
      await api.post(`/invoices/${inv.id}/reminders/send-now`, null,
                     { params: { template_key: reminderTemplate } });
      toast.success("Relance envoyée");
      setReminderOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setReminderSending(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data.invoice);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  const loadPayments = async () => {
    try {
      const { data } = await api.get(`/invoices/${id}/payments`);
      setPayments(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); loadPayments(); }, [id]);

  const submitPayment = async () => {
    setPaySubmitting(true);
    try {
      const body = {
        amount: Number(payForm.amount),
        method: payForm.method || null,
        reference: payForm.reference || null,
        note: payForm.note || null,
      };
      const { data } = await api.post(`/invoices/${id}/payments`, body);
      toast.success(t("invoices.payments.success"));
      if (data.settled) toast.info(t("invoices.payments.summary.settled"));
      setPayOpen(false);
      setPayForm({ amount: "", method: "virement", reference: "", note: "" });
      await Promise.all([load(), loadPayments()]);
    } catch (e) { toast.error(apiError(e)); }
    finally { setPaySubmitting(false); }
  };

  const removePayment = async (pid) => {
    if (!confirm(t("invoices.payments.confirmDelete"))) return;
    try {
      await api.delete(`/invoices/${id}/payments/${pid}`);
      toast.success("Paiement supprimé");
      await Promise.all([load(), loadPayments()]);
    } catch (e) { toast.error(apiError(e)); }
  };

  const download = async () => {
    try {
      // Utilise le client axios auth (JWT Bearer) pour supporter tous les statuts
      // — en brouillon le PDF est un aperçu (numéro "BROUILLON-xxx") ; à
      // l'émission il devient l'original légal Factur-X.
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const isDraft = inv?.status === "draft";
      a.download = `${isDraft ? "APERÇU-" : "Facture-"}${inv?.number || "brouillon"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openPdfPreview = async () => {
    // Ouvre le PDF dans un nouvel onglet (inline) — utile pour aperçu brouillon
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      // Le blob URL est révoqué automatiquement à la fermeture de l'onglet
    } catch (e) { toast.error(apiError(e)); }
  };

  const markPaid = async () => {
    try {
      await api.post(`/invoices/${id}/mark-paid`);
      toast.success("Facture marquée payée");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitCreditNote = async () => {
    setCnSubmitting(true);
    try {
      const { data } = await api.post(`/invoices/${id}/credit-note`, {
        reason: cnReason || null,
      });
      toast.success(t("invoices.creditNote.success", { number: data.credit_note.number }));
      setCnOpen(false);
      setCnReason("");
      navigate(`/app/invoices/${data.credit_note.id}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setCnSubmitting(false); }
  };

  if (loading) return <div className="p-10 text-sm text-slate-500">{t("common.loading")}</div>;
  if (!inv) return null;

  const s = inv.seller_snapshot;
  const isCreditNote = inv.kind === "credit_note";
  const hasCreditNote = !!inv.credit_note_id;
  const canIssueCreditNote =
    !isCreditNote && !hasCreditNote && ["issued", "paid"].includes(inv.status) && canWrite;

  return (
    <div className="space-y-6" data-testid="invoice-view">
      <div className="flex items-center gap-3">
        <Link to="/app/invoices">
          <Button variant="ghost" size="sm" className="text-slate-500"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Button>
        </Link>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
                style={{ fontFamily: "Manrope, sans-serif" }}>
              {inv.number
                ? (isCreditNote
                    ? t("invoices.view.creditNoteTitle", { number: inv.number })
                    : t("invoices.view.title", { number: inv.number }))
                : t("invoices.view.draftBadge")}
            </h1>
            {isCreditNote && (
              <Badge variant="outline" className="text-[10px] uppercase bg-orange-50 text-orange-700 border-orange-200"
                     data-testid="kind-avoir-badge">
                {t("invoices.kind.credit_note")}
              </Badge>
            )}
            <Badge variant="outline" className={cn("text-[10px] uppercase", STATUS_STYLE[inv.status])}>
              {t(`invoices.status.${inv.status}`)}
            </Badge>
          </div>
          <div className="mt-2 text-sm text-slate-500 flex flex-wrap gap-3">
            {inv.issued_at && <span>{t("invoices.view.issuedOn")} {fmtDate(inv.issued_at)}</span>}
            {inv.due_date && !isCreditNote && <span>· {t("invoices.view.dueOn")} {fmtDate(inv.due_date)}</span>}
            {inv.paid_at && <span>· {t("invoices.view.paidOn")} {fmtDate(inv.paid_at)}</span>}
            {inv.cancelled_at && <span>· {t("invoices.view.cancelledOn")} {fmtDate(inv.cancelled_at)}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {inv.status === "draft" && canWrite && (
            <Link to={`/app/invoices/${id}/edit`}>
              <Button variant="outline" data-testid="edit-draft-btn"
                       className="border-blue-200 text-blue-700 hover:bg-blue-50">
                <FileText className="h-4 w-4 mr-1" /> Modifier
              </Button>
            </Link>
          )}
          {inv.status === "draft" && (
            <Button variant="outline" onClick={openPdfPreview} data-testid="preview-pdf-btn"
                    className="border-violet-200 text-violet-700 hover:bg-violet-50">
              <Eye className="h-4 w-4 mr-1" /> Aperçu PDF
            </Button>
          )}
          <Button variant="outline" onClick={download} data-testid="download-pdf-btn"
                  className="border-slate-200">
            <Download className="h-4 w-4 mr-1" /> {inv.status === "draft"
              ? "Télécharger (aperçu)"
              : t("invoices.view.downloadPdf")}
          </Button>
          {!isCreditNote && inv.status === "issued" && (canValidate || canWrite) && (
            <Button onClick={markPaid} data-testid="mark-paid-btn"
                    className="bg-green-600 hover:bg-green-700 text-white">
              <CheckCircle2 className="h-4 w-4 mr-1" /> {t("invoices.view.markPaid")}
            </Button>
          )}
          {!isCreditNote && inv.status === "issued" && canWrite && (inv.client?.email) && (
            <Button variant="outline" onClick={() => setReminderOpen(true)}
                     data-testid="manual-reminder-btn"
                     className="border-amber-200 text-amber-800 hover:bg-amber-50">
              <Bell className="h-4 w-4 mr-1" /> Envoyer relance
            </Button>
          )}
          {canIssueCreditNote && (
            <Button variant="outline" onClick={() => setCnOpen(true)} data-testid="credit-note-btn"
                    className="border-orange-200 text-orange-700 hover:bg-orange-50">
              <RotateCcw className="h-4 w-4 mr-1" /> {t("invoices.view.creditNoteBtn")}
            </Button>
          )}
        </div>
      </div>

      {hasCreditNote && (
        <Alert className="border-orange-200 bg-orange-50" data-testid="has-credit-note-banner">
          <RotateCcw className="h-4 w-4 text-orange-700" />
          <AlertDescription className="text-orange-900 text-xs flex items-center gap-2">
            {t("invoices.creditNote.hasCreditNote")}
            <Link to={`/app/invoices/${inv.credit_note_id}`}
                  className="font-medium underline decoration-orange-400"
                  data-testid="link-to-credit-note">
              voir l'avoir
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {isCreditNote && inv.references_invoice_id && (
        <Alert className="border-orange-200 bg-orange-50" data-testid="credit-note-ref-banner">
          <FileText className="h-4 w-4 text-orange-700" />
          <AlertDescription className="text-orange-900 text-xs flex items-center gap-2">
            {t("invoices.creditNote.isCreditNoteFor")}
            <Link to={`/app/invoices/${inv.references_invoice_id}`}
                  className="font-medium underline decoration-orange-400"
                  data-testid="link-to-original-invoice">
              {inv.references_invoice_number}
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {inv.number && (
        <Alert className="border-blue-200 bg-blue-50">
          <Lock className="h-4 w-4 text-blue-700" />
          <AlertDescription className="text-blue-900 text-xs">
            {t("invoices.view.numberFrozen", { date: fmtDate(inv.issued_at) })}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-slate-200 bg-white rounded-xl p-5" data-testid="seller-block">
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500 mb-3">
            {t("invoices.view.seller")}
          </div>
          {s ? (
            <div className="text-sm text-slate-800 space-y-0.5">
              <div className="font-semibold">{s.name}</div>
              {s.legal_form && <div className="text-slate-500 text-xs">
                {s.legal_form}{s.share_capital_eur ? ` — capital ${fmtEuro(s.share_capital_eur)}` : ""}
              </div>}
              <div>{s.address}</div>
              <div>{s.postal_code} {s.city}</div>
              <div>{s.country}</div>
              <div className="text-xs text-slate-500 mt-2">
                {s.siret ? `SIRET ${s.siret}` : s.siren ? `SIREN ${s.siren}` : ""}
                {s.rcs_city ? ` · RCS ${s.rcs_city}` : ""}
                {s.ape_code ? ` · APE ${s.ape_code}` : ""}
              </div>
              {s.vat_number && <div className="text-xs text-slate-500">TVA {s.vat_number}</div>}
            </div>
          ) : inv.status === "draft" ? (
            <div className="text-xs text-slate-400 italic">
              Snapshot non figé (brouillon) — sera figé à l'émission.
            </div>
          ) : (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 leading-relaxed">
              <b>Instantané émetteur absent.</b> Cette facture a été importée
              d'un connecteur (Axonaut/Pennylane) ou générée avant l'ajout du
              snapshot. Configure tes coordonnées légales dans{" "}
              <a href="/app/settings/billing" className="underline text-amber-800 font-medium">Coordonnées légales</a>
              {" "}(menu latéral en bas), puis relance le backfill via <code className="bg-amber-100 px-1 rounded text-[10px]">POST /api/admin/invoices/backfill-seller-snapshot</code>.
            </div>
          )}
        </Card>

        <Card className="border-slate-200 bg-white rounded-xl p-5" data-testid="client-block">
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500 mb-3">
            {t("invoices.view.client")}
          </div>
          <div className="text-sm text-slate-800 space-y-0.5">
            <div className="font-semibold">{inv.client.name}</div>
            <div>{inv.client.address}</div>
            <div>{inv.client.postal_code} {inv.client.city}</div>
            <div>{inv.client.country}</div>
            {(inv.client.siret || inv.client.siren) && (
              <div className="text-xs text-slate-500 mt-2">
                {inv.client.siret ? `SIRET ${inv.client.siret}` : `SIREN ${inv.client.siren}`}
              </div>
            )}
            {inv.client.vat_number && <div className="text-xs text-slate-500">TVA {inv.client.vat_number}</div>}
          </div>
        </Card>
      </div>

      <Card className="border-slate-200 bg-white rounded-xl overflow-hidden" data-testid="view-lines-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Désignation</TableHead>
              <TableHead className="text-right w-20">Qté</TableHead>
              <TableHead className="text-right w-28">PU HT</TableHead>
              <TableHead className="w-20">TVA</TableHead>
              <TableHead className="text-right w-28">Total HT</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {inv.lines.map((ln) => {
              const net = +(ln.quantity * ln.unit_price_ht * (1 - (ln.discount_percent || 0) / 100)).toFixed(2);
              return (
                <TableRow key={ln.id}>
                  <TableCell>{ln.description}</TableCell>
                  <TableCell className="text-right tabular-nums">{ln.quantity}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtEuro(ln.unit_price_ht)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{ln.vat_rate}%</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtEuro(net)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Payments received — manual log OR auto-reconciled from bank sync */}
      {!isCreditNote && (
        <Card className="border-slate-200 bg-white rounded-xl overflow-hidden" data-testid="payments-card">
          <div className="p-5 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Wallet className="h-4 w-4 text-slate-500" />
                <h2 className="text-lg font-medium text-slate-800">{t("invoices.payments.title")}</h2>
                {/* Badge statut rapprochement — visible seulement pour factures émises */}
                {inv.status === "issued" && (
                  <Badge variant="outline" className={cn("text-[10px] uppercase",
                    payments?.paid_amount_ttc >= (payments?.total_ttc || 0) - 0.01
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-amber-50 text-amber-700 border-amber-200")}>
                    {payments?.paid_amount_ttc >= (payments?.total_ttc || 0) - 0.01
                      ? "✓ Rapproché"
                      : "En attente de rapprochement"}
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-xs text-slate-500">{t("invoices.payments.subtitle")}</p>
            </div>
            {payments && (
              <div className="text-right text-xs text-slate-600 space-y-0.5">
                <div>{t("invoices.payments.summary.total")} : <span className="tabular-nums font-medium text-slate-900">{fmtEuro(payments.total_ttc)}</span></div>
                {payments.credited_amount_ttc > 0 && (
                  <div className="text-orange-700">{t("invoices.payments.summary.credited")} : − <span className="tabular-nums">{fmtEuro(payments.credited_amount_ttc)}</span></div>
                )}
                <div>{t("invoices.payments.summary.paid")} : − <span className="tabular-nums text-green-700">{fmtEuro(payments.paid_amount_ttc)}</span></div>
                <div className="pt-1 border-t border-slate-200 mt-1 font-semibold text-slate-900">
                  {t("invoices.payments.summary.remaining")} : <span className="tabular-nums" data-testid="remaining-due">{fmtEuro(payments.remaining_ttc)}</span>
                </div>
              </div>
            )}
          </div>
          {(payments?.payments?.length || 0) === 0 ? (
            <div className="px-5 pb-5 text-sm text-slate-500 italic">{t("invoices.payments.none")}</div>
          ) : (
            <Table data-testid="payments-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("invoices.payments.col.date")}</TableHead>
                  <TableHead className="text-right">{t("invoices.payments.col.amount")}</TableHead>
                  <TableHead>{t("invoices.payments.col.method")}</TableHead>
                  <TableHead>{t("invoices.payments.col.reference")}</TableHead>
                  <TableHead>{t("invoices.payments.col.by")}</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.payments.map((p) => (
                  <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                    <TableCell className="text-sm text-slate-700">{fmtDate(p.received_at)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium text-green-700">{fmtEuro(p.amount)}</TableCell>
                    <TableCell className="text-xs text-slate-600">{p.method || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{p.reference || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{p.recorded_by_email || "—"}</TableCell>
                    <TableCell className="text-right">
                      {canWrite && (
                        <Button variant="ghost" size="icon"
                                onClick={() => removePayment(p.id)}
                                data-testid={`remove-payment-${p.id}`}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {canWrite && payments && payments.remaining_ttc > 0.009 && (
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
              <Button onClick={() => { setPayForm({ ...payForm, amount: String(payments.remaining_ttc) }); setPayOpen(true); }}
                      data-testid="record-payment-btn"
                      className="bg-green-600 hover:bg-green-700 text-white">
                <Plus className="h-4 w-4 mr-1" /> {t("invoices.payments.recordBtn")}
              </Button>
            </div>
          )}
        </Card>
      )}

      <div className="flex justify-end">
        <Card className="border-slate-200 bg-white rounded-xl p-5 min-w-[280px] space-y-2 text-sm">
          <div className="flex justify-between text-slate-600">
            <span>Sous-total HT</span>
            <span className="tabular-nums">{fmtEuro(inv.totals.subtotal_ht)}</span>
          </div>
          {inv.totals.vat_by_rate.map((v) => (
            <div key={v.rate} className="flex justify-between text-slate-500 text-xs">
              <span>TVA {v.rate}%</span>
              <span className="tabular-nums">{fmtEuro(v.vat_amount)}</span>
            </div>
          ))}
          <div className="pt-3 border-t border-slate-200 flex justify-between text-slate-900 font-semibold text-base">
            <span>{isCreditNote ? t("invoices.creditNote.creditedAmount") : "Total TTC"}</span>
            <span className={cn("tabular-nums", isCreditNote && "text-orange-700")}
                  data-testid="view-ttc">
              {isCreditNote && "− "}{fmtEuro(inv.totals.total_ttc)}
            </span>
          </div>
        </Card>
      </div>

      {/* Credit note dialog */}
      <Dialog open={cnOpen} onOpenChange={setCnOpen}>        <DialogContent data-testid="credit-note-dialog" className="max-w-md">
          <DialogHeader>
            <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center mb-2">
              <RotateCcw className="h-5 w-5 text-orange-600" />
            </div>
            <DialogTitle>{t("invoices.creditNote.title")}</DialogTitle>
            <DialogDescription>{t("invoices.creditNote.confirm")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>{t("invoices.creditNote.reasonLabel")}</Label>
            <Textarea rows={3} value={cnReason} onChange={(e) => setCnReason(e.target.value)}
                      placeholder={t("invoices.creditNote.reasonPlaceholder")}
                      data-testid="credit-note-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCnOpen(false)} data-testid="credit-note-cancel">
              {t("common.cancel")}
            </Button>
            <Button onClick={submitCreditNote} disabled={cnSubmitting}
                    data-testid="credit-note-submit"
                    className="bg-orange-600 hover:bg-orange-700 text-white">
              {cnSubmitting ? t("common.loading") : t("invoices.creditNote.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="payment-dialog" className="max-w-md">
          <DialogHeader>
            <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center mb-2">
              <Wallet className="h-5 w-5 text-green-600" />
            </div>
            <DialogTitle>{t("invoices.payments.dialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("invoices.payments.dialogDesc", { remaining: fmtEuro(payments?.remaining_ttc || 0) })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("invoices.payments.amount")}</Label>
              <Input type="number" step="0.01" min="0.01" value={payForm.amount}
                     onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                     data-testid="payment-amount" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("invoices.payments.methodField")}</Label>
                <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                  <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="virement">{t("invoices.payments.method.virement")}</SelectItem>
                    <SelectItem value="cheque">{t("invoices.payments.method.cheque")}</SelectItem>
                    <SelectItem value="cash">{t("invoices.payments.method.cash")}</SelectItem>
                    <SelectItem value="other">{t("invoices.payments.method.other")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("invoices.payments.referenceField")}</Label>
                <Input value={payForm.reference}
                       onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })}
                       data-testid="payment-reference" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("invoices.payments.noteField")}</Label>
              <Input value={payForm.note}
                     onChange={(e) => setPayForm({ ...payForm, note: e.target.value })}
                     data-testid="payment-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={submitPayment} disabled={paySubmitting || !payForm.amount}
                    data-testid="payment-submit"
                    className="bg-green-600 hover:bg-green-700 text-white">
              {paySubmitting ? t("common.loading") : t("invoices.payments.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual reminder dialog */}
      <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <DialogContent data-testid="manual-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"
                          style={{ fontFamily: "Manrope, sans-serif" }}>
              <Bell className="h-5 w-5 text-amber-600" />
              Envoyer une relance
            </DialogTitle>
            <DialogDescription>
              Envoi immédiat au client — hors échéancier automatique.
              Le message part vers <b className="font-mono text-slate-700">{inv?.client?.email}</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs text-slate-500">Ton du message</Label>
            <Select value={reminderTemplate} onValueChange={setReminderTemplate}>
              <SelectTrigger data-testid="manual-reminder-template">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="friendly">Amical — premier rappel</SelectItem>
                <SelectItem value="firm">Ferme — rappel appuyé</SelectItem>
                <SelectItem value="formal_notice">Mise en demeure — dernier rappel</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReminderOpen(false)}>Annuler</Button>
            <Button onClick={sendManualReminder} disabled={reminderSending}
                     className="bg-amber-600 hover:bg-amber-700 text-white"
                     data-testid="manual-reminder-send-btn">
              <Send className="h-4 w-4 mr-1.5" />
              {reminderSending ? "Envoi…" : "Envoyer maintenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
